import React from "react";
import {Box, Flex, Heading, Text} from "@chakra-ui/react";
import {ExistingSoundOption} from "./SoundFileLibraryDialog";

export const SOUND_LIBRARY_DRAG_TYPE = "application/x-soundspots-library-sound";

export type SoundLibraryDragPayload = {
	originalFileName: string;
	fileName: string;
	filePath: string;
};

type Props = {
	isOpen: boolean;
	existingSoundOptions: ExistingSoundOption[];
	isLoading: boolean;
	onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
	onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
	title?: string;
	openWidth?: string;
};

export const getSoundLibraryDragPayload = (dataTransfer?: DataTransfer | null): SoundLibraryDragPayload | undefined => {
	if (!dataTransfer) {
		return undefined;
	}
	const types = Array.from(dataTransfer.types || []);
	if (!types.includes(SOUND_LIBRARY_DRAG_TYPE)) {
		return undefined;
	}
	const value = dataTransfer.getData(SOUND_LIBRARY_DRAG_TYPE);
	if (!value) {
		return undefined;
	}
	try {
		const parsed = JSON.parse(value) as Partial<SoundLibraryDragPayload>;
		if (!parsed.fileName) {
			return undefined;
		}
		return {
			originalFileName: parsed.originalFileName || parsed.fileName,
			fileName: parsed.fileName,
			filePath: parsed.filePath || "SoundFiles"
		};
	} catch (error) {
		console.warn("Unable to parse sound library drag payload", error);
		const fallbackFileName = dataTransfer.getData("text/plain");
		if (!fallbackFileName) {
			return undefined;
		}
		return {
			originalFileName: fallbackFileName,
			fileName: fallbackFileName,
			filePath: "SoundFiles"
		};
	}
};

export const hasSoundLibraryDragType = (dataTransfer?: DataTransfer | null): boolean => {
	if (!dataTransfer) {
		return false;
	}
	return Array.from(dataTransfer.types || []).includes(SOUND_LIBRARY_DRAG_TYPE);
};

const SoundFileDrawer = ({
	isOpen,
	existingSoundOptions,
	isLoading,
	onDragOver,
	onDrop,
	title = "Available Sound Files",
	openWidth = "25%"
}: Props) => {
	const visibleOptions = existingSoundOptions.filter((option) => !option.libraryEntry?.purgedAt);
	return (
		<Box
			width={isOpen ? openWidth : "0px"}
			minW={0}
			overflow={"hidden"}
			transition={"width 0.25s ease"}
			borderLeftWidth={isOpen ? "1px" : "0px"}
			borderColor={"gray.200"}
			bg={"white"}
			display={"flex"}
			flexDirection={"column"}
			onDragOver={onDragOver}
			onDrop={onDrop}
		>
			<Flex
				px={4}
				py={3}
				borderBottomWidth={"1px"}
				borderColor={"gray.200"}
				align={"center"}
				justify={"space-between"}
				flexShrink={0}
			>
				<Heading size={"xs"} color={"gray.700"}>{title}</Heading>
				<Text fontSize={"xs"} color={"gray.500"}>
					{visibleOptions.length}
				</Text>
			</Flex>
			<Box flex={1} minH={0} overflowY={"auto"}>
				{isLoading ? (
					<Text fontSize={"sm"} color={"gray.500"} px={4} py={3}>Loading sound files...</Text>
				) : visibleOptions.length === 0 ? (
					<Text fontSize={"sm"} color={"gray.500"} px={4} py={3}>No sound files found.</Text>
				) : (
					visibleOptions.map((option) => {
						const originalFileName = option.sound.originalFileName || option.sound.fileName || "Unnamed file";
						const storedFileName = option.sound.fileName || "";
						const storedFilePath = option.sound.filePath || "SoundFiles";
						return (
							<Box
								key={storedFileName || originalFileName}
								px={4}
								py={3}
								borderBottomWidth={"1px"}
								borderColor={"gray.100"}
								cursor={storedFileName ? "grab" : "default"}
								draggable={!!storedFileName}
								onDragStart={(event) => {
									if (!storedFileName) {
										return;
									}
									event.dataTransfer.effectAllowed = "copy";
									event.dataTransfer.setData(SOUND_LIBRARY_DRAG_TYPE, JSON.stringify({
										originalFileName,
										fileName: storedFileName,
										filePath: storedFilePath
									} as SoundLibraryDragPayload));
									event.dataTransfer.setData("text/plain", storedFileName);
								}}
							>
								<Text fontSize={"sm"} fontWeight={"medium"} color={"gray.700"} noOfLines={1}>
									{originalFileName}
								</Text>
								{storedFileName ? (
									<Text fontSize={"xs"} color={"gray.500"} noOfLines={1}>
										{storedFileName}
									</Text>
								) : null}
							</Box>
						);
					})
				)}
			</Box>
		</Box>
	);
};

export default SoundFileDrawer;
